import { Radio as RadioPrimitive } from "@base-ui/react/radio";
import { RadioGroup as RadioGroupPrimitive } from "@base-ui/react/radio-group";
import { cn } from "@maker/utils/cn";
import { useId } from "react";
import { buttonVariants } from "./button";

const RadioLabel = ({ className, ...props }: React.ComponentProps<"div">) => {
	return (
		<div
			className={cn(
				"mb-2 select-none font-semibold text-neutral-700 text-xs leading-none",
				className,
			)}
			{...props}
		/>
	);
};

const RadioInput = ({
	className,
	children,
	...props
}: RadioPrimitive.Root.Props) => {
	const id = useId();

	return (
		<label className="flex items-center gap-2" htmlFor={id}>
			<RadioPrimitive.Root
				className="flex size-3 shrink-0 items-center justify-center rounded-full bg-neutral-700 p-0 outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
				id={id}
				{...props}
			>
				<RadioPrimitive.Indicator className="flex items-center justify-center before:size-1.25 before:rounded-full before:bg-white data-unchecked:hidden" />
			</RadioPrimitive.Root>

			<span className="font-medium text-neutral-900 text-sm">{children}</span>
		</label>
	);
};

const RadioToggleInput = ({
	className,
	children,
	...props
}: RadioPrimitive.Root.Props) => {
	const id = useId();

	return (
		<label
			className="border-neutral-200 border-r last-of-type:border-r-0"
			htmlFor={id}
		>
			<RadioPrimitive.Root
				className={buttonVariants({
					variant: "white",
					size: "sm",
					className:
						"relative rounded-none border-0 bg-neutral-50 focus-visible:ring-2 focus-visible:ring-inset data-disabled:cursor-not-allowed data-checked:bg-white",
				})}
				id={id}
				{...props}
			>
				{children}
			</RadioPrimitive.Root>
		</label>
	);
};

const Radio = ({
	children,
	label,
	...props
}: RadioGroupPrimitive.Props & { label?: string }) => {
	return (
		<RadioGroupPrimitive {...props}>
			{label && <RadioLabel>{label}</RadioLabel>}

			{children}
		</RadioGroupPrimitive>
	);
};

const RadioToggle = ({
	children,
	label,
	...props
}: RadioGroupPrimitive.Props & { label?: string }) => {
	return (
		<RadioGroupPrimitive {...props}>
			{label && <RadioLabel>{label}</RadioLabel>}

			<div className="relative flex w-fit overflow-hidden rounded border border-neutral-200">
				{children}
			</div>
		</RadioGroupPrimitive>
	);
};

export { RadioInput, RadioToggleInput, Radio, RadioToggle };
